"use client";

// TWT's own sign-in code, the one way in by email on the dashboard wall and
// in the checkout sheet. The member enters an email; one email from TWT
// carries six digits and a Sign in button (src/pages/api/login/code/*). The
// sixth digit submits the code; a correct code (or the email's button, from
// any tab) becomes a Clerk session and then the TWT session.
//
// Laid out as the sign-in modal's form (the markup and classes of
// WhopEmailLoginForm's modal layout, styled by SignInModalStyles.tsx): the
// email step, then six cells over one native one-time-code field, paste
// fills six, a wrong code shakes and clears, Resend counts down 30 seconds,
// and the back arrow sits in the panel's corner.
//
// While the code step is open the tab listens for the email's button being
// used in another tab of this browser, and asks again whenever it regains
// focus, so it moves on by itself once that tab has signed in.

import { OtpCodeInput } from "~/components/auth/OtpCodeInput";
import { inboxFor } from "~/components/auth/inboxLink";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  prefetchSignInDestination,
  replaceWithSignInDestination,
  useRouterIfMounted,
} from "~/components/auth/afterSignInNavigation";
import {
  checkLoginCode,
  completeCodeSignIn,
  finishCodeSignIn,
  listenForCodeSignIn,
  maskEmailForDisplay,
  requestLoginCode,
  verifyLoginCode,
  waitForClerk,
  type ClerkTicketSession,
  type CodeSignIn,
} from "~/components/auth/loginCodeClient";
import {
  SignInStepBackControl,
  SignInStepFrame,
} from "~/components/auth/SignInStepFrame";
import {
  AlertIcon,
  ButtonSpinner,
  ChevronLeftIcon,
  EnvelopeIcon,
} from "~/components/auth/signInModalIcons";
import { safeRedirectPath } from "~/utils/safeRedirectPath";

const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
  /** Where the sign-in lands: the wall's destination, or the checkout's own
   * return path so it reopens at the next step with the same plan. */
  nextPath: string;
  /** Null while Clerk loads; a code is only checked once it has. */
  clerk: ClerkTicketSession | null;
  initialEmail?: string;
  initialError?: string;
  /** Continue in place once the TWT session exists (the checkout). Without
   * it the page moves to the destination. */
  onSignedIn?: () => void | Promise<void>;
  onCodeStepChange?: (codeStep: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
  /** A containing dialog's Cancel on the email step. */
  onCancel?: () => void;
  emailLabel?: string;
  /** Show "Enter the 6-digit code sent to …" over the cells. The modal
   * panels carry their own line, so there it is for screen readers only. */
  showSentTo?: boolean;
  /** Animate the step change here unless a containing panel animates it. */
  animateSteps?: boolean;
  disabled?: boolean;
};

type Status = "idle" | "sending" | "verifying" | "finishing";

export function EmailCodeSignIn({
  nextPath,
  clerk,
  initialEmail = "",
  initialError = "",
  onSignedIn,
  onCodeStepChange,
  onBusyChange,
  onCancel,
  emailLabel = "Email",
  showSentTo = false,
  animateSteps = true,
  disabled = false,
}: Props) {
  const router = useRouterIfMounted();
  const fieldId = useId();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(initialEmail);
  const [sentEmail, setSentEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [flowToken, setFlowToken] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState(initialError);
  const [shake, setShake] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">(
    "idle"
  );
  const [pendingSwitch, setPendingSwitch] = useState<{
    signIn: CodeSignIn;
    signedInEmail: string | null;
  } | null>(null);
  const [retrySignIn, setRetrySignIn] = useState<CodeSignIn | null>(null);

  const mountedRef = useRef(true);
  const clerkRef = useRef(clerk);
  clerkRef.current = clerk;
  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;
  const nextPathRef = useRef(nextPath);
  nextPathRef.current = nextPath;
  const statusRef = useRef<Status>("idle");
  statusRef.current = status;
  const flowTokenRef = useRef("");
  flowTokenRef.current = flowToken;
  const codeRef = useRef("");
  // Bumped by every new send and by going back, so a late answer to an older
  // request never paints over the step the member is on.
  const generationRef = useRef(0);
  const sendLockRef = useRef(false);
  const verifyLockRef = useRef(false);
  const submittedCodeRef = useRef("");
  const movedRef = useRef(false);
  const lastCheckRef = useRef(0);
  const handoffsRef = useRef(new Set<string>());
  const codeInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeHelpId = `${fieldId}-help`;
  const codeErrorId = `${fieldId}-error`;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onCodeStepChange?.(step === "code");
  }, [step, onCodeStepChange]);

  useEffect(() => {
    onBusyChange?.(status === "verifying" || status === "finishing");
  }, [status, onBusyChange]);

  // Entering the code step, and each new code, puts the cursor in the cells.
  useEffect(() => {
    if (step === "code") codeInputRef.current?.focus();
  }, [step, flowToken]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(
      () => setResendSeconds((seconds) => Math.max(0, seconds - 1)),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (resendState !== "sent") return;
    const timer = window.setTimeout(() => setResendState("idle"), 4000);
    return () => window.clearTimeout(timer);
  }, [resendState]);

  // A refused code shakes the cells once; the class comes off when the
  // animation ends, or after the same time when reduced motion runs none.
  useEffect(() => {
    if (!shake) return;
    codeInputRef.current?.focus();
    const timer = window.setTimeout(() => setShake(false), 450);
    return () => window.clearTimeout(timer);
  }, [shake]);

  const moveForward = useCallback(
    async (redirectTo: string) => {
      if (movedRef.current) return;
      movedRef.current = true;
      setStatus("finishing");
      const continueInPlace = onSignedInRef.current;
      if (continueInPlace) {
        try {
          await continueInPlace();
        } finally {
          movedRef.current = false;
          if (mountedRef.current) setStatus("idle");
        }
        return;
      }
      await replaceWithSignInDestination(
        router,
        window.location,
        safeRedirectPath(redirectTo, nextPathRef.current)
      );
    },
    [router]
  );

  // The email's button, used in another tab of this browser.
  useEffect(() => {
    if (step !== "code") return;
    return listenForCodeSignIn({
      handoffs: () => handoffsRef.current,
      onSignedIn: (redirectTo) =>
        void moveForward(redirectTo || nextPathRef.current),
    });
  }, [step, moveForward]);

  // Coming back to this tab: was the code's link used, and is this browser
  // signed in now?
  useEffect(() => {
    if (step !== "code") return;
    const check = () => {
      if (document.visibilityState === "hidden") return;
      const token = flowTokenRef.current;
      if (!token || movedRef.current || statusRef.current !== "idle") return;
      const now = Date.now();
      if (now - lastCheckRef.current < 1500) return;
      lastCheckRef.current = now;
      void checkLoginCode(token)
        .then(({ body }) => {
          if (!mountedRef.current || flowTokenRef.current !== token) return;
          if (!body.ok || !("used" in body) || !body.used) return;
          if (body.signedIn) {
            void moveForward(nextPathRef.current);
            return;
          }
          setFlowToken("");
          setCode("");
          codeRef.current = "";
          setResendSeconds(0);
          setError("That code was already used. Request a new code.");
        })
        .catch(() => undefined);
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [step, moveForward]);

  function updateCode(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    codeRef.current = digits;
    if (error && digits) setError("");
    if (digits.length < 6) submittedCodeRef.current = "";
    if (
      digits.length === 6 &&
      submittedCodeRef.current !== digits &&
      flowTokenRef.current &&
      statusRef.current === "idle"
    ) {
      submittedCodeRef.current = digits;
      void submitCode(digits, flowTokenRef.current);
    }
  }

  async function finish(signIn: CodeSignIn, switchAccount: boolean) {
    const clerkSession = clerkRef.current;
    if (!clerkSession) {
      setStatus("idle");
      setError("Sign-in is still loading. Try again in a moment.");
      return;
    }
    setStatus("finishing");
    const outcome = await finishCodeSignIn({
      clerk: clerkSession,
      signIn,
      switchAccount,
    });
    if (!mountedRef.current) return;
    if (outcome.kind === "signed_in") {
      await moveForward(outcome.redirectTo);
      return;
    }
    setStatus("idle");
    if (outcome.kind === "switch_account") {
      setPendingSwitch({ signIn, signedInEmail: outcome.signedInEmail });
      return;
    }
    setError(outcome.message);
    if (outcome.retry === "complete") {
      setRetrySignIn(signIn);
    } else {
      setFlowToken("");
      setCode("");
      codeRef.current = "";
      setResendSeconds(0);
    }
  }

  async function submitCode(value: string, token: string) {
    if (value.length !== 6 || !token || verifyLockRef.current) return;
    verifyLockRef.current = true;
    const generation = generationRef.current;
    setStatus("verifying");
    setError("");
    // Assume the code is right: the destination's code loads alongside the
    // check. The checkout continues in place and goes nowhere.
    if (!onSignedIn) prefetchSignInDestination(router, nextPath);
    try {
      // The code is spent the moment it is accepted, so it is only sent once
      // Clerk is ready to take the ticket.
      const clerkSession = await waitForClerk(() => clerkRef.current);
      if (!mountedRef.current || generation !== generationRef.current) return;
      if (!clerkSession) {
        submittedCodeRef.current = "";
        setStatus("idle");
        setError("Sign-in is still loading. Try again in a moment.");
        return;
      }
      const result = await verifyLoginCode({ flowToken: token, code: value });
      if (!mountedRef.current || generation !== generationRef.current) return;
      const body = result.body;
      if (!body.ok) {
        setStatus("idle");
        setCode("");
        codeRef.current = "";
        submittedCodeRef.current = "";
        setError(body.message || "We couldn't check that code. Try again.");
        if (body.code === "wrong_code") {
          setShake(true);
        } else if (
          body.code === "expired" ||
          body.code === "used" ||
          body.code === "too_many_attempts"
        ) {
          // This code is finished; Resend is the way on.
          setFlowToken("");
          setResendSeconds(0);
        }
        return;
      }
      await finish(body, false);
    } catch {
      if (!mountedRef.current || generation !== generationRef.current) return;
      submittedCodeRef.current = "";
      setStatus("idle");
      setError("We couldn't check that code. Try again.");
    } finally {
      verifyLockRef.current = false;
    }
  }

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    if (disabled || sendLockRef.current || statusRef.current !== "idle") return;
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalized)) {
      setError("Enter a valid email address.");
      emailInputRef.current?.focus();
      return;
    }
    sendLockRef.current = true;
    const generation = ++generationRef.current;
    // The code step shows the moment Continue is pressed; digits typed while
    // the email goes out are kept and checked as soon as it has.
    setError("");
    setPendingSwitch(null);
    setRetrySignIn(null);
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    setFlowToken("");
    flowTokenRef.current = "";
    handoffsRef.current.clear();
    setSentTo(maskEmailForDisplay(normalized));
    setStatus("sending");
    setStep("code");
    try {
      const result = await requestLoginCode({
        email: normalized,
        next: nextPath,
      });
      if (!mountedRef.current || generation !== generationRef.current) return;
      const body = result.body;
      if (!body.ok) {
        setStatus("idle");
        setStep("email");
        setError(
          body.message || "We couldn't email you a code. Please try again."
        );
        emailInputRef.current?.focus();
        return;
      }
      handoffsRef.current.add(body.handoff);
      setSentEmail(normalized);
      setSentTo(body.email);
      setFlowToken(body.flowToken);
      flowTokenRef.current = body.flowToken;
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
      setStatus("idle");
      statusRef.current = "idle";
      const buffered = codeRef.current;
      if (buffered.length === 6 && submittedCodeRef.current !== buffered) {
        submittedCodeRef.current = buffered;
        void submitCode(buffered, body.flowToken);
      }
    } catch {
      if (!mountedRef.current || generation !== generationRef.current) return;
      setStatus("idle");
      setStep("email");
      setError("We couldn't email you a code. Please try again.");
    } finally {
      sendLockRef.current = false;
    }
  }

  async function resend() {
    if (
      status !== "idle" ||
      resendSeconds > 0 ||
      resendState === "sending" ||
      !sentEmail
    ) {
      return;
    }
    const generation = ++generationRef.current;
    setResendState("sending");
    setError("");
    setPendingSwitch(null);
    setRetrySignIn(null);
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    try {
      const result = await requestLoginCode({
        email: sentEmail,
        next: nextPath,
      });
      if (!mountedRef.current || generation !== generationRef.current) return;
      const body = result.body;
      if (!body.ok) {
        setResendState("idle");
        setError(
          body.message || "We couldn't send a new code. Please try again."
        );
        return;
      }
      handoffsRef.current.add(body.handoff);
      setFlowToken(body.flowToken);
      setSentTo(body.email);
      setResendState("sent");
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
    } catch {
      if (!mountedRef.current || generation !== generationRef.current) return;
      setResendState("idle");
      setError("We couldn't send a new code. Please try again.");
    }
  }

  async function confirmSwitch() {
    const pending = pendingSwitch;
    if (!pending || statusRef.current !== "idle") return;
    setPendingSwitch(null);
    await finish(pending.signIn, true);
  }

  async function retryCompletion() {
    const signIn = retrySignIn;
    if (!signIn || statusRef.current !== "idle") return;
    setRetrySignIn(null);
    setError("");
    setStatus("finishing");
    const outcome = await completeCodeSignIn(signIn);
    if (!mountedRef.current) return;
    if (outcome.kind === "signed_in") {
      await moveForward(outcome.redirectTo);
      return;
    }
    setStatus("idle");
    if (outcome.kind === "error") {
      setError(outcome.message);
      if (outcome.retry === "complete") {
        setRetrySignIn(signIn);
      } else {
        setFlowToken("");
        flowTokenRef.current = "";
        setCode("");
        codeRef.current = "";
        setResendSeconds(0);
      }
    }
  }

  function backToEmail() {
    if (status === "verifying" || status === "finishing") return;
    generationRef.current += 1;
    sendLockRef.current = false;
    setStep("email");
    setStatus("idle");
    setFlowToken("");
    flowTokenRef.current = "";
    handoffsRef.current.clear();
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    setError("");
    setPendingSwitch(null);
    setRetrySignIn(null);
    setResendState("idle");
    setResendSeconds(0);
    window.setTimeout(() => emailInputRef.current?.focus(), 0);
  }

  const busy = status === "verifying" || status === "finishing";
  const sending = status === "sending";
  const inbox = inboxFor(sentTo);
  const codeFieldDisabled =
    busy || resendState === "sending" || (!flowToken && !sending);

  let body: JSX.Element;
  if (step === "code") {
    const progress = sending
      ? "Sending code"
      : status === "verifying"
      ? "Checking code"
      : status === "finishing"
      ? "Signing in"
      : "";
    // The code step's cell rules sit in its own tree: styled-jsx scopes a
    // style block to the JSX it is written in.
    body = (
      <>
        <form
          className="whop-oauth-form whop-oauth-code-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (code.length === 6 && flowToken && status === "idle") {
              submittedCodeRef.current = code;
              void submitCode(code, flowToken);
            }
          }}
          aria-busy={busy || sending}
        >
          <SignInStepBackControl>
            <button
              type="button"
              className="twt-modal-back whop-oauth-switch"
              disabled={busy}
              onClick={backToEmail}
            >
              <ChevronLeftIcon />
              <span className="twt-visually-hidden">Use a different email</span>
            </button>
          </SignInStepBackControl>
          <div
            className={`whop-oauth-code-head${
              showSentTo ? "" : " twt-visually-hidden"
            }`}
          >
            <p className="whop-oauth-code-sentto" id={codeHelpId}>
              {sending
                ? "Sending a 6-digit code to "
                : "Enter the 6-digit code sent to "}
              <span>{sentTo || "your email"}</span>.
            </p>
          </div>

          {error ? (
            <p
              className="whop-oauth-error whop-oauth-code-error"
              id={codeErrorId}
              role="alert"
            >
              <AlertIcon />
              <span>{error}</span>
            </p>
          ) : null}

          {pendingSwitch ? (
            <>
              <div className="whop-oauth-code-head">
                <p className="whop-oauth-code-sentto">
                  This browser is signed in as{" "}
                  <span>
                    {pendingSwitch.signedInEmail || "another account"}
                  </span>
                  . Continuing will switch to{" "}
                  <span>{sentTo || "your email"}</span>.
                </p>
              </div>
              <div className="whop-oauth-actions">
                <button
                  type="button"
                  className="whop-oauth-approve"
                  onClick={() => void confirmSwitch()}
                >
                  Switch account
                </button>
              </div>
            </>
          ) : retrySignIn ? (
            <div className="whop-oauth-actions">
              <button
                type="button"
                className="whop-oauth-approve"
                disabled={busy}
                onClick={() => void retryCompletion()}
              >
                Try again
              </button>
            </div>
          ) : (
            <div
              className={`whop-oauth-code-wrap${shake ? " is-shaking" : ""}`}
              onAnimationEnd={(event) => {
                if (event.target === event.currentTarget) setShake(false);
              }}
            >
              <OtpCodeInput
                ref={codeInputRef}
                id={fieldId}
                value={code}
                onChange={updateCode}
                disabled={codeFieldDisabled}
                invalid={Boolean(error)}
                describedBy={`${codeHelpId}${error ? ` ${codeErrorId}` : ""}`}
                errorMessageId={codeErrorId}
              />
            </div>
          )}

          {inbox && !pendingSwitch && !retrySignIn ? (
            <a
              className="twt-otp-inbox"
              href={inbox.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <EnvelopeIcon size={17} />
              {inbox.label}
            </a>
          ) : null}

          {progress ? (
            <p
              className="whop-oauth-code-status"
              role="status"
              aria-live="polite"
            >
              <ButtonSpinner />
              <span>{progress}</span>
            </p>
          ) : null}

          {pendingSwitch || retrySignIn ? null : (
            <p className="whop-oauth-resend">
              Didn&apos;t receive a code?{" "}
              <button
                type="button"
                onClick={() => void resend()}
                disabled={
                  busy ||
                  sending ||
                  resendSeconds > 0 ||
                  resendState === "sending"
                }
                className="whop-oauth-resend-btn"
              >
                {resendState === "sending"
                  ? "Sending..."
                  : resendSeconds > 0
                  ? `Resend in ${resendSeconds}s`
                  : "Resend"}
              </button>
              {resendState === "sent" ? (
                <span
                  className="whop-oauth-resend-ok"
                  role="status"
                  aria-live="polite"
                >
                  New code sent.
                </span>
              ) : null}
            </p>
          )}
        </form>
        <style jsx>{`
          .whop-oauth-code-form {
            width: 100%;
            max-width: 440px;
            gap: 18px;
          }
          .whop-oauth-code-head {
            gap: 6px;
          }
          .whop-oauth-code-sentto {
            color: var(--twt-ink-soft, #2a2a26);
            font-size: 15px;
            line-height: 1.45;
            text-wrap: pretty;
          }
          .whop-oauth-code-sentto span {
            color: var(--twt-ink, #141413);
            font-weight: 700;
            overflow-wrap: anywhere;
          }
          .whop-oauth-code-wrap {
            cursor: text;
          }
          .twt-otp-inbox {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            min-height: 44px;
            margin-top: -2px;
            border-radius: 10px;
            background: var(--twt-canvas, #faf9f5);
            box-shadow: inset 0 0 0 1px var(--twt-hairline, #e8e6dc);
            color: var(--twt-ink, #141413);
            font-size: var(--twt-text-body-size);
            line-height: var(--twt-text-body-line);
            font-weight: 500;
            text-decoration: none;
            transition: background 150ms ease, transform 120ms ease;
          }
          .twt-otp-inbox:hover {
            background: var(--twt-ground, #f0eee6);
          }
          .twt-otp-inbox:active {
            transform: scale(0.99);
          }
          .twt-otp-inbox:focus-visible {
            outline: 2px solid var(--twt-copper, #bf7550);
            outline-offset: 3px;
          }
          .whop-oauth-code-input {
            z-index: 1;
            border: 0;
            border-radius: 8px;
            font-size: 16px;
            caret-color: transparent;
          }
          .whop-oauth-code-input:focus {
            outline: none;
          }
          /* The cells read the surface's variables, so the sign-in modal
             (SignInModalStyles.tsx, .twt-signin-modal) sets its own cell
             design without out-specifying these scoped rules. */
          .whop-oauth-code-grid {
            gap: var(--twt-code-cell-gap, 8px);
          }
          .whop-oauth-code-grid div {
            min-width: 0;
            min-height: var(--twt-code-cell-height, 52px);
            aspect-ratio: auto;
            border-color: var(--twt-hairline, #e8e6dc);
            border-radius: var(--twt-code-cell-radius, 8px);
            background: var(--twt-card, #fff);
            box-shadow: var(--twt-code-cell-shadow, none);
            color: var(--twt-ink, #141413);
            font-size: var(--twt-code-cell-font-size, 20px);
            font-variant-numeric: tabular-nums;
            font-weight: 650;
            transition: border-color 150ms ease, background 150ms ease,
              box-shadow 150ms ease;
          }
          .whop-oauth-code-grid div.is-filled {
            border-color: var(--twt-code-cell-filled-border, #d8d5c4);
            background: var(
              --twt-code-cell-filled-bg,
              var(--twt-canvas, #faf9f5)
            );
          }
          .whop-oauth-code-grid div.is-active {
            border-color: var(--twt-code-cell-filled-border, #d8d5c4);
            box-shadow: var(--twt-code-cell-shadow, none);
          }
          .whop-oauth-code-wrap:focus-within
            .whop-oauth-code-grid
            div.is-active {
            border-color: var(
              --twt-code-cell-focus-border,
              var(--twt-clay, #d97757)
            );
            box-shadow: var(
              --twt-code-cell-focus-shadow,
              0 0 0 2px rgb(217 119 87 / 18%)
            );
          }
          .whop-oauth-code-grid.is-invalid div {
            border-color: var(
              --twt-code-cell-invalid-border,
              rgb(196 67 47 / 45%)
            );
            box-shadow: var(
              --twt-code-cell-invalid-shadow,
              var(--twt-code-cell-shadow, none)
            );
          }
          .whop-oauth-code-wrap:focus-within
            .whop-oauth-code-grid.is-invalid
            div.is-active {
            border-color: var(
              --twt-code-cell-invalid-focus-border,
              var(--twt-oxblood, #c4432f)
            );
            box-shadow: var(
              --twt-code-cell-invalid-focus-shadow,
              0 0 0 2px rgb(196 67 47 / 16%)
            );
          }
          .whop-oauth-code-error {
            color: var(--twt-oxblood-deep, #8a2f1f);
          }
          .whop-oauth-resend-ok {
            color: var(--twt-sage-deep, #475a32);
          }
          .whop-oauth-resend-btn:focus-visible,
          .whop-oauth-switch:focus-visible,
          .whop-oauth-approve:focus-visible,
          .whop-oauth-cancel:focus-visible {
            border-radius: 3px;
            outline: 2px solid var(--twt-clay, #d97757);
            outline-offset: 3px;
          }
          @media (max-width: 380px) {
            .whop-oauth-code-form {
              gap: 16px;
            }
            .whop-oauth-code-grid {
              gap: 5px;
            }
            .whop-oauth-code-grid div {
              min-height: 44px;
              font-size: 18px;
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .whop-oauth-code-grid div {
              transition: none;
            }
          }
        `}</style>
      </>
    );
  } else {
    body = (
      <form className="whop-oauth-form" onSubmit={submitEmail} noValidate>
        <div className="whop-oauth-secondary">
          <label
            className="whop-oauth-email-label"
            htmlFor={`${fieldId}-email`}
          >
            {emailLabel}
          </label>
          <div className="whop-oauth-email-wrap">
            <EnvelopeIcon className="whop-oauth-email-icon" />
            <input
              ref={emailInputRef}
              id={`${fieldId}-email`}
              name="email"
              aria-label="Email address"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${fieldId}-email-error` : undefined}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              disabled={disabled}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError("");
              }}
              placeholder="name@example.com"
              type="email"
              className="whop-oauth-email-field"
            />
          </div>
        </div>

        {error ? (
          <p
            id={`${fieldId}-email-error`}
            className="whop-oauth-error"
            role="alert"
          >
            <AlertIcon />
            <span>{error}</span>
          </p>
        ) : null}

        <div className="whop-oauth-actions">
          {onCancel ? (
            <button
              type="button"
              className="whop-oauth-cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="submit"
            disabled={disabled}
            className="whop-oauth-approve"
          >
            Continue
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      {animateSteps ? (
        <SignInStepFrame
          stepKey={step}
          direction={step === "code" ? "forward" : "back"}
        >
          {body}
        </SignInStepFrame>
      ) : (
        body
      )}
    </>
  );
}
